import { useEffect, useRef, useState } from "react";

// How often the creep/catch-up ticker steps, in ms. Short enough (with the
// matching CSS transition duration used wherever this is rendered) that each
// step blends into a continuous-looking motion rather than visible jumps.
const TICK_MS = 200;
// Fraction of the remaining gap closed per tick once a NEW, meaningfully
// higher checkpoint arrives from the backend — a fast-decelerating catch-up,
// not an instant snap (which would feel robotic) and not a slow crawl either
// (which would feel laggy right after real progress just happened).
const CATCH_UP_FRACTION = 0.3;
// Percentage points added per tick while idling at/near the last known real
// checkpoint, waiting for the next one.
const CREEP_STEP = 0.15;
// How far past the last known real percent the idle creep is allowed to
// drift at the normal CREEP_STEP rate before slowing down (see
// STALL_CREEP_STEP below) — keeps the bar visibly alive during a long gap
// between real updates (this is exactly the "stuck at 92% for minutes"
// complaint) without ever claiming meaningfully more progress than backend
// truth actually supports.
const CREEP_BUFFER = 4;
// Once CREEP_BUFFER is exhausted, keep drifting toward CREEP_CEILING at this
// much slower rate instead of stopping dead. A real checkpoint is normally
// only UPLOAD_STATUS_POLL_MS/RECORDS_POLL_MS (a few seconds) away, well
// inside CREEP_BUFFER's reach — but startBackoffPolling doubles that gap on
// every consecutive failure (see pollingBackoff.ts's own docstring on this
// app's flaky dev tunnel), and a hard stop here reads as the spinner/progress
// genuinely freezing for however long that backoff-lengthened wait lasts,
// then snapping forward the moment the delayed poll finally lands — exactly
// the "stuck, then automatically started spinning again" complaint this hook
// was already meant to prevent. A slow trickle keeps it visibly alive through
// that gap too, without claiming real progress the backend hasn't confirmed.
const STALL_CREEP_STEP = 0.03;
// ...but never more than this far past the real number. Unbounded, the
// trickle reached 99 during any long real phase (reading the text of a
// 1,000-page file takes ~10 minutes) while the backend said ~40%, and the
// next page load then showed the real number: the bar "went back" to 70.
const STALL_LIMIT = 8;
// Hard ceiling for the creep — real completion (100%) must always come from
// an actual terminal status, never be simulated into existence here.
const CREEP_CEILING = 99;

/**
 * Turns a series of real, occasionally-jumpy backend checkpoints (polled
 * every few seconds, arriving in big discrete steps — e.g. 20% for a while,
 * then suddenly 55%) into continuous-looking motion: creep slowly while
 * waiting on the current checkpoint, catch up faster (but not instantly)
 * the moment a new, higher one arrives, then resume the slow creep — repeat
 * until `active` goes false (the real, terminal value should be shown
 * directly at that point, not through this hook).
 *
 * The displayed value creeps up to CREEP_BUFFER points past the last known
 * real checkpoint at the normal rate, then (only if a long gap — e.g. a
 * backoff-lengthened poll retry — keeps the next real checkpoint from
 * arriving) keeps trickling slower still, up to STALL_LIMIT points past it;
 * it never reaches 100 on its own and never moves backwards, even across a
 * remount (pass `uploadKey`) — this is smoothing of real data, not a
 * substitute for it.
 */
// What each upload's bar last showed, kept across remounts (leaving the page
// and coming back, the list reloading): a remount used to start again from
// the real number, below what was already on screen.
const shownByUpload = new Map<string, number>();

export function useSmoothedUploadProgress(target: number | undefined, active: boolean, uploadKey?: string): number {
  const [displayed, setDisplayed] = useState(() =>
    Math.max(target ?? 5, (uploadKey && shownByUpload.get(uploadKey)) || 0),
  );
  const targetRef = useRef(target ?? 5);

  useEffect(() => {
    if (!uploadKey) return;
    if (active) shownByUpload.set(uploadKey, displayed);
    else shownByUpload.delete(uploadKey);
  }, [uploadKey, active, displayed]);

  useEffect(() => {
    if (target !== undefined) targetRef.current = target;
  }, [target]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => {
      setDisplayed((prev) => {
        const t = targetRef.current;
        if (prev < t) {
          const gap = t - prev;
          const step = gap > CREEP_BUFFER ? Math.max(gap * CATCH_UP_FRACTION, 0.5) : Math.min(CREEP_STEP, gap);
          return Math.min(t, prev + step);
        }
        if (prev < t + CREEP_BUFFER) {
          return Math.min(prev + CREEP_STEP, t + CREEP_BUFFER, CREEP_CEILING);
        }
        // Buffer exhausted with no new checkpoint yet (a backoff-lengthened
        // gap) — keep trickling instead of freezing solid, within STALL_LIMIT.
        if (prev >= t + STALL_LIMIT) return prev;
        return Math.min(prev + STALL_CREEP_STEP, t + STALL_LIMIT, CREEP_CEILING);
      });
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [active]);

  return displayed;
}
